import { CalendarOutlined, CloseCircleFilled } from '@ant-design/icons';
import { useTranslation } from '@framework/i18n';
import { DatePicker, DatePickerProps, TimePicker } from 'antd';
import dayjs from 'dayjs';
import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import { useInputController } from '../hooks/useInputController';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility';
import { useFormItemEnabled } from '../item/useFormItemEnabled';

type DateInputType = 'iso8601' | 'datetime' | 'date' | 'time';
export type IDateInputProps = DatePickerProps & {
  name: string;
  type?: DateInputType;
  /**
   * Formatto per l'input del datepicker (default: 'DD/MM/YYYY')
   */
  format?: string;
  /**
   * Aggungiamo proprietà readOnly non gestita da antd
   * (gestita solo sul campi input con proprietà inputReadOnly)
   * per i campi di tipo data
   */
  readOnly?: boolean;
};

const ISO8601_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS';
const DATETIME_FORMAT = 'YYYY-MM-DD[T]HH:mm:ss';
const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'HH:mm:ss';

const getFormat = (type?: DateInputType) => {
  switch (type) {
    case 'iso8601':
      return ISO8601_FORMAT;
    case 'datetime':
      return DATETIME_FORMAT;
    case 'time':
      return TIME_FORMAT;
    case 'date':
    default:
      return DATE_FORMAT;
  }
};

export function DateInput(props: IDateInputProps) {
  const {
    name,
    type,
    readOnly,
    format: datePickerFormat = 'DD/MM/YYYY',
    ...inputProps
  } = props;
  const { t } = useTranslation();

  const { enabled, layoutEnabled } = useFormItemEnabled();

  const {
    field: { value, onChange, onBlur, ref },
  } = useInputController(name, { enabled });

  const { setError } = useFormContext();

  const accessibility = useFormItemAccessibility();

  // Assicurarsi di censire tutti i formati anche nello useMemo di `inputValue`
  const format = getFormat(type);

  const inputValue = React.useMemo(() => {
    const dateValue = inputProps.value ?? value;

    if (!dateValue) return undefined;

    const date = dayjs(dateValue, [
      ISO8601_FORMAT,
      DATETIME_FORMAT,
      DATE_FORMAT,
      TIME_FORMAT,
    ]);
    if (!date.isValid()) return undefined;
    return date;
  }, [inputProps.value, value]);

  // Scegliamo il picker in base al tipo
  const Picker = type === 'time' ? TimePicker : DatePicker;

  return (
    <Picker
      suffixIcon={<CalendarOutlined aria-hidden="true" />}
      size="large"
      style={{ width: '100%' }}
      format={datePickerFormat}
      disabled={enabled === false || layoutEnabled === false}
      {...accessibility.attributes}
      {...inputProps}
      value={inputValue}
      ref={ref}
      name={name}
      preserveInvalidOnBlur
      onBlur={e => {
        // Andiamo a gestire un onChange sull'onBlur per convertire la data e mantenere
        // visibile il valore anche in caso di data non valida (richiesta per l'accessibilità)
        // Il check controlla che l'evento di onBlur non provenga dal pannello del datepicker
        // ma dalla modifica "a mano" e diretta dell'input stesso.
        // Se richiamassimo la funzione all'onBlur, il componente per qualche motivo richiama la onChange
        // con un valore errato quando si sceglie una data di un mese diverso da quello visualizzato ("data grigia")
        // (probabilmente il bug è dovuto al fatto che internamente il datePicker utilizzi un indice o qualcosa di simile
        // per calcolare la data selezionata, e non la data vera e propria)
        if (
          e.target instanceof HTMLInputElement &&
          !e.relatedTarget?.className.includes('ant-picker-panel')
        ) {
          // // Se il campo è vuoto, lo imposto a null
          if (!e.target.value) {
            onChange(null);
            onBlur();
            return;
          }

          // Se il campo non è vuoto, controllo che sia una data valida
          // e la salvo nel formato corretto
          const dateValue = dayjs(
            e.target.value,
            [datePickerFormat, 'D/M/YYYY H:m:s', 'D/M/YYYY H:m', 'D/M/YYYY'],
            true,
          );

          if (dateValue.isValid()) {
            // Se non chiamassimo qui la funzione il valore non verrebbe aggiornato.
            onChange(dateValue ? dateValue.format(format) : null);
          } else {
            // Ci assicuriamo che il campo venga marcato come "toccato", così da mostrare l'errore
            onBlur();

            // Questa gestione permette di mostrare l'errore solo dopo che il
            // campo è stato "toccato". Non è perfetta perché permette comunque
            // l'invio del form con il _vecchio_ valore registrato, ma è
            // un compromesso per non rompere la UX.
            setTimeout(() => {
              setError(name, {
                type: 'manual',
                message: t(
                  'inputs.date-input.error',
                  'La data inserita non è valida',
                ),
              });
            }, 1);
          }
        }

        onBlur();
      }}
      onChange={value => {
        onChange(value ? value.format(format) : null);
      }}
      allowClear={
        readOnly
          ? false
          : {
              clearIcon: (
                <CloseCircleFilled
                  // per essere sicuri sia accessibile
                  aria-hidden="false"
                  aria-label={t(
                    'inputs.date-input.clear-button',
                    'Pulisci il campo',
                  )}
                />
              ),
            }
      }
      inputReadOnly={readOnly}
      // se il campo è readonly non mostriamo il pannello di selezione
      panelRender={readOnly ? panelProps => null : undefined}
    />
  );
}
